import { z } from 'zod';

const NonEmptyTextSchema = z.string().trim().min(1);
const StableIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/);

export const ParagraphIdSchema = z.string().regex(/^p_[A-Za-z0-9]+$/);

export const SourceKindSchema = z.enum([
  'official-church',
  'church-news',
  'ai-analysis',
  'sci',
]);

export const ProvenanceSchema = z.object({
  sourceKind: SourceKindSchema,
  sourceId: NonEmptyTextSchema,
  sourceUrl: z.string().url().optional(),
  evidenceParagraphIds: z.array(ParagraphIdSchema).default([]),
});

export const ScriptureSelectionSchema = z.object({
  bookId: StableIdSchema,
  bookName: NonEmptyTextSchema,
  chapter: z.number().int().positive(),
  verses: z.array(z.number().int().positive()).min(1),
  displayLabel: NonEmptyTextSchema,
  officialChurchUrl: z.string().url(),
  sciUrl: z.string().url(),
});

export const FootnoteSchema = z.object({
  number: z.number().int().positive(),
  paragraphId: ParagraphIdSchema,
  source: z.literal('official'),
  label: NonEmptyTextSchema.optional(),
  sourceUrl: z.string().url().optional(),
  scriptures: z.array(ScriptureSelectionSchema).default([]),
});

export const ParagraphSchema = z.object({
  id: ParagraphIdSchema,
  index: z.number().int().nonnegative(),
  kind: z.enum(['paragraph', 'heading']).default('paragraph'),
  text: NonEmptyTextSchema,
  footnoteNumbers: z.array(z.number().int().positive()).default([]),
});

export const AiRecallReferenceSchema = z.object({
  referenceId: StableIdSchema,
  paragraphId: ParagraphIdSchema.optional(),
  source: z.literal('ai-recall'),
  displayLabel: NonEmptyTextSchema,
  referenceType: NonEmptyTextSchema,
  confidence: z.enum(['high', 'medium-high', 'medium', 'low', 'unknown']),
  provenance: ProvenanceSchema.refine(
    (value) => value.sourceKind === 'ai-analysis',
    'AI recall references must identify AI analysis as their source.',
  ),
});

export const AnalysisReferenceSchema = z.object({
  analysisId: StableIdSchema,
  kind: z.enum([
    'theme',
    'sentiment',
    'rhetoric',
    'quote',
    'story',
    'call-to-action',
    'summary',
  ]),
  provenance: ProvenanceSchema,
});

export const TalkSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  talkId: StableIdSchema,
  conferenceId: StableIdSchema,
  title: NonEmptyTextSchema,
  speaker: z.object({
    name: NonEmptyTextSchema,
    calling: z.string(),
  }),
  session: z.object({
    code: StableIdSchema,
    name: NonEmptyTextSchema,
    order: z.number().int().nonnegative(),
  }),
  sourceUrl: z.string().url(),
  summary: z
    .object({
      text: NonEmptyTextSchema,
      attribution: NonEmptyTextSchema,
      sourceUrl: z.string().url(),
    })
    .optional(),
  paragraphs: z.array(ParagraphSchema),
  footnotes: z.array(FootnoteSchema),
  recallReferences: z.array(AiRecallReferenceSchema),
  analysisReferences: z.array(AnalysisReferenceSchema),
});

export const ConferenceTalkSummarySchema = z.object({
  talkId: StableIdSchema,
  title: NonEmptyTextSchema,
  speaker: z.object({
    name: NonEmptyTextSchema,
    calling: z.string(),
  }),
  session: z.object({
    code: StableIdSchema,
    name: NonEmptyTextSchema,
    order: z.number().int().nonnegative(),
  }),
  sourceUrl: z.string().url(),
  excerpt: NonEmptyTextSchema,
  paragraphCount: z.number().int().positive(),
  officialFootnoteCount: z.number().int().nonnegative(),
  topThemes: z.array(
    z.object({
      name: NonEmptyTextSchema,
      slug: StableIdSchema,
      prominence: z.number().int().nonnegative(),
    }),
  ),
});

export const ConferenceIndexSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  datasetVersion: NonEmptyTextSchema,
  builtAt: z.string().datetime(),
  conferenceId: StableIdSchema,
  title: NonEmptyTextSchema,
  dates: NonEmptyTextSchema,
  talkCount: z.number().int().positive(),
  officialFootnoteCount: z.number().int().nonnegative(),
  uniqueThemeCount: z.number().int().positive(),
  featuredThemes: z.array(
    z.object({
      name: NonEmptyTextSchema,
      slug: StableIdSchema,
      talkCount: z.number().int().positive(),
      totalProminence: z.number().int().positive(),
    }),
  ),
  talks: z.array(ConferenceTalkSummarySchema).min(1),
}).superRefine((index, context) => {
  if (index.talkCount !== index.talks.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Talk count must match the conference talk list.',
      path: ['talkCount'],
    });
  }
});

export const ConferenceCatalogEntrySchema = z.object({
  conferenceId: StableIdSchema,
  title: NonEmptyTextSchema,
  dates: NonEmptyTextSchema,
  talkCount: z.number().int().positive(),
  datasetVersion: NonEmptyTextSchema,
  dataPath: z.string().startsWith('/data/conference/'),
});

export const ConferenceCatalogSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  defaultConferenceId: StableIdSchema,
  conferences: z.array(ConferenceCatalogEntrySchema).min(1),
}).superRefine((catalog, context) => {
  if (!catalog.conferences.some(
    (conference) => conference.conferenceId === catalog.defaultConferenceId,
  )) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Default conference must appear in the catalog.',
      path: ['defaultConferenceId'],
    });
  }
});

export const AnalysisItemSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  analysisId: StableIdSchema,
  talkId: StableIdSchema,
  kind: AnalysisReferenceSchema.shape.kind,
  label: NonEmptyTextSchema,
  body: NonEmptyTextSchema,
  score: z.number().optional(),
  provenance: ProvenanceSchema.refine(
    (value) => value.sourceKind === 'ai-analysis',
    'Analysis items must identify AI analysis as their source.',
  ),
});

export const ReaderThemeSchema = z.object({
  name: NonEmptyTextSchema,
  slug: StableIdSchema,
  prominence: z.number().int().nonnegative(),
  subThemes: z.array(NonEmptyTextSchema),
  evidence: z.array(
    z.object({
      text: NonEmptyTextSchema,
      paragraphId: ParagraphIdSchema,
    }),
  ),
});

export const ReaderSentimentSchema = z.object({
  label: NonEmptyTextSchema,
  secondaryLabel: z.string(),
  intensity: z.number().int().min(0).max(10),
  valence: z.enum(['positive', 'neutral', 'negative', 'mixed']),
  description: NonEmptyTextSchema,
  paragraphIds: z.array(ParagraphIdSchema).min(1),
});

export const ReaderTalkAnalysisSchema = z.object({
  talkId: StableIdSchema,
  sourceKind: z.literal('ai-analysis'),
  themes: z.array(ReaderThemeSchema),
  sentiment: z.array(ReaderSentimentSchema),
});

export const ReaderAnalysisIndexSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  conferenceId: StableIdSchema,
  sourceKind: z.literal('ai-analysis'),
  talks: z.record(StableIdSchema, ReaderTalkAnalysisSchema),
});

export const GeneratedCopySchema = z.object({
  text: NonEmptyTextSchema,
  aiGenerated: z.literal(true),
});

export const ThreadExcerptSchema = z.object({
  paragraphId: ParagraphIdSchema,
  text: NonEmptyTextSchema,
});

export const ThreadStepSchema = z.object({
  talkId: StableIdSchema,
  title: NonEmptyTextSchema,
  speaker: NonEmptyTextSchema,
  sessionOrder: z.number().int().nonnegative(),
  readingMinutes: z.number().int().positive(),
  excerpt: z.array(ThreadExcerptSchema).min(1).max(3),
  prominence: z.number().int().nonnegative().optional(),
  currentConferenceOccurrences: z.number().int().positive().optional(),
});

export const ThreadConnectionSchema = z.object({
  fromTalkId: StableIdSchema,
  toTalkId: StableIdSchema,
  note: NonEmptyTextSchema,
  aiGenerated: z.literal(true),
});

const ThreadBaseSchema = z.object({
  threadId: StableIdSchema,
  title: NonEmptyTextSchema,
  subtitle: NonEmptyTextSchema,
  intro: GeneratedCopySchema,
  estimatedMinutes: z.number().int().positive(),
  steps: z.array(ThreadStepSchema).min(1),
  connections: z.array(ThreadConnectionSchema),
  relatedThreadIds: z.array(StableIdSchema).min(1).max(3),
});

export const ThemeThreadSchema = ThreadBaseSchema.extend({
  type: z.literal('theme'),
  orderRule: z.literal('prominence-desc'),
});

export const VerseThreadSchema = ThreadBaseSchema.extend({
  type: z.literal('verse'),
  orderRule: z.literal('current-conference-relevance'),
  scripture: ScriptureSelectionSchema,
  currentConferenceOccurrenceCount: z.number().int().positive(),
});

export const SpeakerThreadSchema = ThreadBaseSchema.extend({
  type: z.literal('speaker'),
  orderRule: z.literal('session-order'),
});

export const GuidedThreadSchema = z.discriminatedUnion('type', [
  ThemeThreadSchema,
  VerseThreadSchema,
  SpeakerThreadSchema,
]);

export const ThreadsIndexSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  conferenceId: StableIdSchema,
  datasetVersion: NonEmptyTextSchema,
  sciDatasetVersion: z.literal('sci-2026-07-27-41d08183b0a4'),
  sourceKind: z.literal('generated-analysis'),
  themeThreads: z.array(ThemeThreadSchema).min(1),
  verseThreads: z.array(VerseThreadSchema).min(1),
  speakerThreads: z.array(SpeakerThreadSchema).min(1),
});

export const ReflectionQuestionSchema = z.object({
  questionId: StableIdSchema,
  question: NonEmptyTextSchema,
  groundedIn: z.array(ParagraphIdSchema).min(1),
  aiGenerated: z.literal(true),
});

export const ReadNextSuggestionSchema = z.object({
  talkId: StableIdSchema,
  title: NonEmptyTextSchema,
  speaker: NonEmptyTextSchema,
  reason: NonEmptyTextSchema,
  sharedThemes: z.array(NonEmptyTextSchema),
  sharedScriptureKeys: z.array(NonEmptyTextSchema),
  sharedThreadIds: z.array(StableIdSchema),
});

export const TalkReflectionSchema = z.object({
  talkId: StableIdSchema,
  questions: z.array(ReflectionQuestionSchema).min(2).max(3),
  readNext: z.array(ReadNextSuggestionSchema).min(2).max(3),
});

export const ReflectionsIndexSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  conferenceId: StableIdSchema,
  sourceKind: z.literal('generated-analysis'),
  tone: z.literal('personal-study'),
  talks: z.record(StableIdSchema, TalkReflectionSchema),
});

export const SciCorpusSchema = z.enum([
  'general-conference',
  'journal-of-discourses',
  'tpjs',
]);

export const SciCitationSchema = z.object({
  contextId: StableIdSchema,
  talkId: NonEmptyTextSchema,
  corpus: SciCorpusSchema,
  year: z.number().int().min(1800).max(2200).nullable(),
  session: z.string(),
  page: z.string(),
  speaker: NonEmptyTextSchema,
  title: z.string(),
  watchUrl: z.string().url().nullable(),
  listenUrl: z.string().url().nullable(),
  rawReference: NonEmptyTextSchema,
});

export const SciRangeSchema = z.object({
  rangeId: StableIdSchema,
  displayRange: NonEmptyTextSchema,
  exactVerses: z.array(z.number().int().positive()),
  sourceBadgeCount: z.number().int().nonnegative(),
  materializedCitationCount: z.number().int().nonnegative(),
  knownGapCount: z.number().int().nonnegative(),
  citations: z.array(SciCitationSchema),
});

export const SciChapterShardSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  datasetVersion: NonEmptyTextSchema,
  bookId: StableIdSchema,
  chapter: z.number().int().positive(),
  corpora: z.array(SciCorpusSchema),
  sourceBadgeCount: z.number().int().nonnegative(),
  materializedCitationCount: z.number().int().nonnegative(),
  ranges: z.array(SciRangeSchema),
  knownGapCount: z.number().int().nonnegative(),
  knownGapRecords: z.number().int().nonnegative(),
});

export const SciBookSchema = z.object({
  bookId: StableIdSchema,
  volume: NonEmptyTextSchema,
  name: NonEmptyTextSchema,
  sourceBadgeCount: z.number().int().nonnegative(),
  knownGapCount: z.number().int().nonnegative(),
  knownGapRecords: z.number().int().nonnegative(),
});

export const SciBooksIndexSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  datasetVersion: NonEmptyTextSchema,
  books: z.array(SciBookSchema),
});

export const SciManifestSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  datasetVersion: NonEmptyTextSchema,
  builtAt: z.string().datetime(),
  source: z.object({
    kind: z.literal('sci-sqlite'),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    attribution: NonEmptyTextSchema,
  }),
  counts: z.object({
    books: z.number().int().positive(),
    chapters: z.number().int().positive(),
    ranges: z.number().int().positive(),
    materializedCitations: z.number().int().positive(),
    distinctContexts: z.number().int().positive(),
    sourceBadgeCitations: z.number().int().positive(),
    knownGapRecords: z.number().int().nonnegative(),
    knownGapCitations: z.number().int().nonnegative(),
    corpora: z.record(SciCorpusSchema, z.number().int().nonnegative()),
  }),
  packaging: z.object({
    chapterShardCount: z.number().int().positive(),
    uncompressedBytes: z.number().int().positive(),
    gzipBytes: z.number().int().positive(),
    largestChapterShard: z.object({
      path: NonEmptyTextSchema,
      bytes: z.number().int().positive(),
      gzipBytes: z.number().int().positive(),
    }),
  }),
  assets: z.array(
    z.object({
      path: NonEmptyTextSchema,
      bytes: z.number().int().nonnegative(),
      gzipBytes: z.number().int().nonnegative(),
      sha256: z.string().regex(/^[a-f0-9]{64}$/),
    }),
  ),
});

export const HighlightColorSchema = z.enum(['yellow', 'blue', 'green', 'pink']);

export const StudyHighlightSchema = z.object({
  id: z.union([z.string(), z.number().int().positive()]),
  userId: z.union([z.string(), z.number().int().positive()]),
  talkId: StableIdSchema,
  paragraphId: ParagraphIdSchema,
  startOffset: z.number().int().nonnegative(),
  endOffset: z.number().int().positive(),
  color: HighlightColorSchema,
  textSnippet: NonEmptyTextSchema,
  createdAt: z.string().datetime(),
});

export const StudyNoteSchema = z.object({
  id: z.union([z.string(), z.number().int().positive()]),
  userId: z.union([z.string(), z.number().int().positive()]),
  talkId: StableIdSchema,
  paragraphId: ParagraphIdSchema.nullable(),
  noteText: NonEmptyTextSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const ReadingProgressSchema = z.object({
  talkId: StableIdSchema,
  paragraphId: ParagraphIdSchema.nullable(),
  percent: z.number().min(0).max(100),
  completed: z.boolean(),
  updatedAt: z.string().datetime(),
});

export const ThreadProgressSchema = z.object({
  conferenceId: StableIdSchema,
  threadId: StableIdSchema,
  completedTalkIds: z.array(StableIdSchema),
  completed: z.boolean(),
  updatedAt: z.string().datetime(),
});

export const StudyBookmarkSchema = z.object({
  id: z.union([z.string(), z.number().int().positive()]),
  userId: z.union([z.string(), z.number().int().positive()]),
  talkId: StableIdSchema,
  createdAt: z.string().datetime(),
});

export const ExploredScriptureSchema = z.object({
  bookId: StableIdSchema,
  chapter: z.number().int().positive(),
  verses: z.array(z.number().int().positive()).min(1),
  exploredAt: z.string().datetime(),
});

export const StudyDataSchema = z.object({
  schemaVersion: z.literal('2.0.0'),
  userId: z.union([z.string(), z.number().int().positive()]),
  highlights: z.array(StudyHighlightSchema),
  notes: z.array(StudyNoteSchema),
  readingProgress: z.array(ReadingProgressSchema),
  threadProgress: z.array(ThreadProgressSchema),
  bookmarks: z.array(StudyBookmarkSchema),
  exploredScriptures: z.array(ExploredScriptureSchema),
});

export const TalkStudyStateSchema = z.object({
  progress: ReadingProgressSchema.nullable(),
  highlights: z.array(StudyHighlightSchema),
  notes: z.array(StudyNoteSchema),
});

export const AssetManifestEntrySchema = z.object({
  id: StableIdSchema,
  sourcePath: NonEmptyTextSchema,
  contentType: NonEmptyTextSchema,
  bytes: z.number().int().nonnegative(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
});

export const AssetManifestSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  datasetVersion: NonEmptyTextSchema,
  builtAt: z.string().datetime(),
  assets: z.array(AssetManifestEntrySchema),
});

export const FsyRelatedTalkPassageSchema = z.object({
  talkId: StableIdSchema,
  title: NonEmptyTextSchema,
  speaker: NonEmptyTextSchema,
  paragraphId: ParagraphIdSchema,
  excerpt: NonEmptyTextSchema,
  href: z.string().startsWith('/read/'),
  score: z.number().min(-1).max(1),
});

function sentenceCount(value: string): number {
  return value.match(/[.!](?:\s|$)/g)?.length ?? 0;
}

export const FsyAnalysisTalkSchema = z
  .object({
    talkId: StableIdSchema,
    title: NonEmptyTextSchema,
    speaker: NonEmptyTextSchema,
    paragraphId: ParagraphIdSchema,
    href: z.string().startsWith('/read/'),
    relevance: NonEmptyTextSchema,
  })
  .superRefine((talk, context) => {
    if (sentenceCount(talk.relevance) !== 2) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FSY analysis relevance blurbs must contain exactly two sentences.',
        path: ['relevance'],
      });
    }
    if (/[\u2013\u2014]|\s-\s/.test(talk.relevance)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FSY analysis relevance blurbs cannot use dash clause separators.',
        path: ['relevance'],
      });
    }
  });

export const FsyAnalysisSectionSchema = z.object({
  id: StableIdSchema,
  blockIndex: z.number().int().nonnegative(),
  itemIndex: z.number().int().nonnegative().optional(),
  talks: z.array(FsyAnalysisTalkSchema).max(3),
});

const FsyParagraphBlockSchema = z.object({
  type: z.literal('paragraph'),
  text: NonEmptyTextSchema,
});

const FsyHeadingBlockSchema = z.object({
  type: z.literal('heading'),
  text: NonEmptyTextSchema,
});

const FsyBulletsBlockSchema = z.object({
  type: z.literal('bullets'),
  items: z.array(NonEmptyTextSchema).min(1),
});

const FsyQuoteBlockSchema = z.object({
  type: z.literal('quote'),
  text: NonEmptyTextSchema,
});

const FsyResourcesBlockSchema = z.object({
  type: z.literal('resources'),
  title: NonEmptyTextSchema,
  items: z.array(NonEmptyTextSchema).min(1),
});

const FsyScriptureTableBlockSchema = z.object({
  type: z.literal('scripture-table'),
  columns: z.tuple([NonEmptyTextSchema, NonEmptyTextSchema, NonEmptyTextSchema]),
  scriptures: z.array(NonEmptyTextSchema).min(1),
});

export const FsyContentBlockSchema = z.discriminatedUnion('type', [
  FsyParagraphBlockSchema,
  FsyHeadingBlockSchema,
  FsyBulletsBlockSchema,
  FsyQuoteBlockSchema,
  FsyResourcesBlockSchema,
  FsyScriptureTableBlockSchema,
]);

export const FsyItemTypeSchema = z.enum([
  'intro',
  'fast-sunday',
  'second-sunday',
  'third-sunday',
  'last-sunday-yw',
  'last-sunday-ap',
  'activity-idea',
]);

export const FsyItemSchema = z
  .object({
    id: StableIdSchema,
    slug: z.string().regex(/^\d{2}[ab]?-[a-z0-9-]+$/),
    type: FsyItemTypeSchema,
    week: z.number().int().min(1).max(4).optional(),
    scheduleLabel: NonEmptyTextSchema.optional(),
    audienceLabel: NonEmptyTextSchema.optional(),
    seriesLabel: NonEmptyTextSchema.optional(),
    title: NonEmptyTextSchema,
    blocks: z.array(FsyContentBlockSchema).min(1),
    sourceUrl: z.string().url(),
    relatedTalks: z.array(FsyRelatedTalkPassageSchema).max(5),
    analysisSections: z.array(FsyAnalysisSectionSchema).default([]),
  })
  .superRefine((item, context) => {
    const ids = new Set<string>();
    for (const [index, section] of item.analysisSections.entries()) {
      if (ids.has(section.id)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate FSY analysis section id: ${section.id}`,
          path: ['analysisSections', index, 'id'],
        });
      }
      ids.add(section.id);
      const block = item.blocks.at(section.blockIndex);
      if (block === undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'FSY analysis section references a missing content block.',
          path: ['analysisSections', index, 'blockIndex'],
        });
      } else if (
        section.itemIndex !== undefined &&
        (!('items' in block) || section.itemIndex >= block.items.length)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'FSY analysis section references a missing list item.',
          path: ['analysisSections', index, 'itemIndex'],
        });
      }
    }
  });

export const FsyMonthSchema = z
  .object({
    id: z.string().regex(/^\d{4}-\d{2}$/),
    year: z.number().int().min(2026).max(2200),
    month: z.number().int().min(1).max(12),
    monthLabel: NonEmptyTextSchema,
    topic: NonEmptyTextSchema,
    guideReference: NonEmptyTextSchema,
    attribution: z.object({
      text: NonEmptyTextSchema,
      sourceUrl: z.string().url(),
    }),
    items: z.array(FsyItemSchema).min(1),
  })
  .superRefine((month, context) => {
    const ids = new Set<string>();
    const slugs = new Set<string>();
    for (const [index, item] of month.items.entries()) {
      if (ids.has(item.id)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate FSY item id: ${item.id}`,
          path: ['items', index, 'id'],
        });
      }
      if (slugs.has(item.slug)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate FSY item slug: ${item.slug}`,
          path: ['items', index, 'slug'],
        });
      }
      ids.add(item.id);
      slugs.add(item.slug);

      const isScheduled = item.type !== 'intro' && item.type !== 'activity-idea';
      if (isScheduled && (!item.week || !item.scheduleLabel)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Scheduled FSY items require a week and schedule label.',
          path: ['items', index],
        });
      }
      if (!isScheduled && (item.week || item.scheduleLabel)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Intro and activity items cannot have a Sunday schedule.',
          path: ['items', index],
        });
      }
    }
  });

export const FsyMonthsSchema = z.array(FsyMonthSchema);

export const RouteNameSchema = z.enum([
  'home',
  'read-index',
  'read-talk',
  'read-paragraph',
  'scripture',
  'threads-index',
  'thread',
  'explore-index',
  'explore-view',
  'search',
  'study',
  'fsy-index',
  'fsy-month',
  'not-found',
]);

const RouteBaseSchema = z.object({
  name: RouteNameSchema,
  pathname: z.string().startsWith('/'),
  params: z.record(z.string()),
});

export const RouteLocationSchema = RouteBaseSchema.superRefine((route, context) => {
  const requiredParameters: Partial<Record<z.infer<typeof RouteNameSchema>, string[]>> = {
    'read-talk': ['talkId'],
    'read-paragraph': ['talkId', 'paragraphId'],
    scripture: ['bookId', 'chapter', 'selection'],
    thread: ['threadId'],
    'explore-view': ['view'],
    'fsy-month': ['monthId'],
  };

  for (const key of requiredParameters[route.name] ?? []) {
    if (!route.params[key]) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Route ${route.name} requires parameter ${key}.`,
        path: ['params', key],
      });
    }
  }
});

export type Talk = z.infer<typeof TalkSchema>;
export type Paragraph = z.infer<typeof ParagraphSchema>;
export type Footnote = z.infer<typeof FootnoteSchema>;
export type ScriptureSelection = z.infer<typeof ScriptureSelectionSchema>;
export type AiRecallReference = z.infer<typeof AiRecallReferenceSchema>;
export type AnalysisItem = z.infer<typeof AnalysisItemSchema>;
export type ReaderTalkAnalysis = z.infer<typeof ReaderTalkAnalysisSchema>;
export type ReaderAnalysisIndex = z.infer<typeof ReaderAnalysisIndexSchema>;
export type GuidedThread = z.infer<typeof GuidedThreadSchema>;
export type ThemeThread = z.infer<typeof ThemeThreadSchema>;
export type VerseThread = z.infer<typeof VerseThreadSchema>;
export type SpeakerThread = z.infer<typeof SpeakerThreadSchema>;
export type ThreadsIndex = z.infer<typeof ThreadsIndexSchema>;
export type TalkReflection = z.infer<typeof TalkReflectionSchema>;
export type ReflectionsIndex = z.infer<typeof ReflectionsIndexSchema>;
export type ConferenceIndex = z.infer<typeof ConferenceIndexSchema>;
export type ConferenceTalkSummary = z.infer<typeof ConferenceTalkSummarySchema>;
export type ConferenceCatalog = z.infer<typeof ConferenceCatalogSchema>;
export type ConferenceCatalogEntry = z.infer<typeof ConferenceCatalogEntrySchema>;
export type SciCitation = z.infer<typeof SciCitationSchema>;
export type SciChapterShard = z.infer<typeof SciChapterShardSchema>;
export type SciCorpus = z.infer<typeof SciCorpusSchema>;
export type SciBook = z.infer<typeof SciBookSchema>;
export type SciBooksIndex = z.infer<typeof SciBooksIndexSchema>;
export type SciManifest = z.infer<typeof SciManifestSchema>;
export type StudyData = z.infer<typeof StudyDataSchema>;
export type StudyHighlight = z.infer<typeof StudyHighlightSchema>;
export type StudyNote = z.infer<typeof StudyNoteSchema>;
export type ReadingProgress = z.infer<typeof ReadingProgressSchema>;
export type ThreadProgress = z.infer<typeof ThreadProgressSchema>;
export type StudyBookmark = z.infer<typeof StudyBookmarkSchema>;
export type ExploredScripture = z.infer<typeof ExploredScriptureSchema>;
export type HighlightColor = z.infer<typeof HighlightColorSchema>;
export type TalkStudyState = z.infer<typeof TalkStudyStateSchema>;
export type AssetManifest = z.infer<typeof AssetManifestSchema>;
export type FsyContentBlock = z.infer<typeof FsyContentBlockSchema>;
export type FsyRelatedTalkPassage = z.infer<typeof FsyRelatedTalkPassageSchema>;
export type FsyAnalysisTalk = z.infer<typeof FsyAnalysisTalkSchema>;
export type FsyAnalysisSection = z.infer<typeof FsyAnalysisSectionSchema>;
export type FsyItemType = z.infer<typeof FsyItemTypeSchema>;
export type FsyItem = z.infer<typeof FsyItemSchema>;
export type FsyMonth = z.infer<typeof FsyMonthSchema>;
export type RouteLocation = z.infer<typeof RouteLocationSchema>;
